import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Play, Heart, Users, Sparkles, ArrowRight, X, CheckCircle2, Shield, Award, Phone, Mail, Send,
  GraduationCap, UtensilsCrossed, Sprout, HandHeart, Quote, Star, MapPin, Landmark, BadgeCheck, FileCheck2,
} from "lucide-react";
import PublicNavbar from "@/components/PublicNavbar";
import { HomeTopBar, HomeHero } from "@/components/home/HomeHero";
import { FocusAreas, ImpactBand } from "@/components/home/FocusAreas";
import PublicFooter from "@/components/PublicFooter";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api } from "@/lib/api";
import { useInView, useCountUp } from "@/lib/hooks";
import { toast } from "sonner";

const PMR_PHOTOS = Array.from({ length: 9 }, (_, i) => `/pmr-photos/pmr-${i + 1}.jpg`);

const QUICK_PROGRAMS = [
  { id: "child-education", icon: GraduationCap, title: "Child Education", color: "from-blue-600 to-indigo-700", img: PMR_PHOTOS[0], stat: "2,500+ Students" },
  { id: "healthcare", icon: Heart, title: "Healthcare Aid", color: "from-rose-500 to-red-600", img: PMR_PHOTOS[5], stat: "10,000+ Patients" },
  { id: "food", icon: UtensilsCrossed, title: "Food Distribution", color: "from-amber-500 to-orange-600", img: PMR_PHOTOS[2], stat: "50,000+ Meals" },
  { id: "women", icon: Users, title: "Women Empowerment", color: "from-violet-600 to-fuchsia-600", img: PMR_PHOTOS[4], stat: "1,000+ Trained" },
  { id: "rural", icon: Sprout, title: "Rural Development", color: "from-emerald-600 to-teal-700", img: PMR_PHOTOS[1], stat: "25+ Villages" },
];

const WHY_PMR = [
  { icon: BadgeCheck, title: "80G Tax Exemption", text: "Every donation is eligible for up to 50% tax deduction under Section 80G of the Income Tax Act.", color: "text-emerald-600 bg-emerald-50" },
  { icon: Shield, title: "100% Transparent", text: "Every rupee is tracked. Monthly impact reports and annual audits published publicly.", color: "text-brand bg-blue-50" },
  { icon: HandHeart, title: "On-Ground Impact", text: "Our volunteers are from the very communities we serve — in 9+ districts across Bihar.", color: "text-[#F59E0B] bg-orange-50" },
  { icon: Award, title: "Section 8 Registered", text: "Govt. of India registered non-profit · CIN U84120BR2024NPL067860 · NGO Darpan verified.", color: "text-violet-600 bg-violet-50" },
];

const STORIES = [
  { name: "Anjali Kumari", role: "Scholarship Recipient, Patna", quote: "PMR ne meri college ki fees bhar di jab meri family kuchh afford nahi kar sakti thi. Aaj main B.Sc. final year mein hoon.", img: PMR_PHOTOS[0] },
  { name: "Ramesh Paswan", role: "Farmer, Vaishali", quote: "Winter mein kambal aur ration mila. Bachchon ko book-bag bhi mili. Yeh log sach mein seva karte hain.", img: PMR_PHOTOS[2] },
  { name: "Sunita Devi", role: "Tailoring Trainee, Nalanda", quote: "6 mahine ke training ke baad ab apni dukaan chalati hoon. PMR ne na sirf skills di, aatmvishwas bhi diya.", img: PMR_PHOTOS[4] },
];

const RECOGNITIONS = [
  { icon: FileCheck2, title: "80G Certified", code: "AAOCP5008QF20251", sub: "Tax exemption" },
  { icon: Landmark, title: "NGO Darpan", code: "BR/2024/0396082", sub: "NITI Aayog" },
  { icon: Award, title: "Section 8 Co.", code: "U84120BR2024NPL067860", sub: "MCA registered" },
  { icon: Shield, title: "12A Registered", code: "Income Tax Dept.", sub: "Since 2024" },
];

export default function Home() {
  const [hp, setHp] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [email, setEmail] = useState("");
  useEffect(() => { api.get("/public/homepage").then((r) => setHp(r.data)).catch(() => {}); }, []);

  const videoUrl = toEmbed(hp?.video_url);
  const phone = hp?.contact_phone || "+91 9155561892";
  const emailAddr = hp?.contact_email || "contact@pmrwelfarefoundation.in";

  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("Thanks! You'll receive updates on our work and impact stories.");
    setEmail("");
  };

  return (
    <div className="min-h-screen bg-white">
      <HomeTopBar phone={phone} email={emailAddr} />
      <PublicNavbar variant="home" />
      <HomeHero subtitle={hp?.hero_subtitle} videoUrl={videoUrl} onWatch={() => setVideoOpen(true)} />
      <FocusAreas />
      <ImpactBand />

      {/* === Why PMR === */}
      <Reveal as="section" className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-block px-4 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-brand text-xs font-black uppercase tracking-[0.2em]">Why PMR Welfare</div>
            <h2 className="heading mt-5 text-3xl sm:text-5xl font-black text-slate-900 leading-tight">
              A foundation you can <span className="text-brand">trust</span>.<br />
              Where every rupee <span className="text-[#F59E0B]">counts</span>.
            </h2>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHY_PMR.map((f, i) => {
              const I = f.icon;
              return (
                <div key={i} className="group bg-white rounded-2xl border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-xl hover:border-brand/30 transition">
                  <div className={`w-12 h-12 rounded-xl ${f.color} flex items-center justify-center mb-4`}>
                    <I size={22} />
                  </div>
                  <h3 className="heading font-black text-lg text-slate-900">{f.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Reveal>

      {/* === About === */}
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div className="grid grid-cols-2 gap-4">
          <img src={PMR_PHOTOS[6]} alt="Activity" className="rounded-2xl aspect-[4/5] object-cover shadow-md hover:shadow-2xl transition" />
          <div className="mt-10">
            <img src={PMR_PHOTOS[3]} alt="Activity" className="rounded-2xl aspect-[4/5] object-cover shadow-md hover:shadow-2xl transition" />
          </div>
        </div>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F59E0B]">About Us</div>
          <h2 className="heading mt-3 text-3xl sm:text-4xl font-black text-slate-900 leading-tight">
            A community that believes in <span className="text-brand">seva</span> over charity.
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            {hp?.about_text || "PMR Welfare Foundation is a registered Section-8 NGO dedicated to empowering underprivileged communities in Bihar and beyond. Through education scholarships, healthcare camps, women's skill training, and rural development programs, we have directly impacted over 10,000 lives since our founding."}
          </p>
          <div className="mt-6 grid grid-cols-3 gap-3">
            <MiniStat k="5+" v="Years of Seva" />
            <MiniStat k="9+" v="Districts" />
            <MiniStat k="24/7" v="Support" />
          </div>
          <Link to="/about" className="mt-8 inline-flex items-center gap-2 font-black text-brand group">
            Read our full story
            <span className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center group-hover:bg-[#F59E0B] group-hover:translate-x-1 transition">
              <ArrowRight size={14} />
            </span>
          </Link>
        </div>
      </Reveal>

      {/* === Leadership / Founders === */}
      <Reveal as="section" className="bg-white py-20 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="text-xs font-black uppercase tracking-[0.25em] text-[#F59E0B]">Our Leadership</div>
            <h2 className="heading mt-3 text-3xl sm:text-4xl font-black">Meet the Founders</h2>
            <p className="mt-3 text-slate-600">The visionaries who lead PMR Welfare Foundation with empathy, dedication, and a deep commitment to community upliftment.</p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <FounderCard
              name={hp?.founder_name || "Aashish Kumar Jha"}
              designation={hp?.founder_designation || "Chairman, PMR Welfare Foundation"}
              photo={hp?.founder_photo_url}
            />
            {hp?.founder2_name && (
              <FounderCard
                name={hp.founder2_name}
                designation={hp?.founder2_designation || "Founder, PMR Welfare Foundation"}
                photo={hp?.founder2_photo_url}
              />
            )}
          </div>
        </div>
      </Reveal>

      {/* === Programs === */}
      <Reveal as="section" className="bg-slate-50 border-y border-slate-200 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.25em] text-[#F59E0B]">What We Do</div>
              <h2 className="heading mt-3 text-3xl sm:text-4xl font-black">Our Programs</h2>
              <p className="mt-3 text-slate-600 max-w-2xl">Comprehensive programs designed to create lasting impact across education, healthcare, nutrition, and community development.</p>
            </div>
            <Link to="/programs" className="text-brand font-black text-sm inline-flex items-center gap-1 hover:text-[#F59E0B]">View all programs <ArrowRight size={14} /></Link>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {QUICK_PROGRAMS.map((p) => {
              const I = p.icon;
              return (
                <Link key={p.id} to={`/programs#${p.id}`} className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-2xl transition" data-testid={`program-${p.id}`}>
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className={`absolute top-4 left-4 w-12 h-12 rounded-xl bg-gradient-to-br ${p.color} text-white flex items-center justify-center shadow-xl`}>
                      <I size={20} />
                    </div>
                    <div className="absolute bottom-3 left-4 right-4 text-white">
                      <h3 className="heading text-lg font-black drop-shadow">{p.title}</h3>
                      <div className="text-[11px] font-bold text-amber-300">{p.stat}</div>
                    </div>
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <span className="text-sm font-bold text-brand">Learn more</span>
                    <span className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-brand group-hover:text-white flex items-center justify-center transition">
                      <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              );
            })}
            <Link to="/programs" className="group rounded-2xl bg-gradient-to-br from-[#1E3A8A] via-[#2447a8] to-[#0b1637] text-white p-7 flex flex-col justify-between hover:-translate-y-1 transition shadow-lg">
              <div>
                <div className="w-14 h-14 rounded-2xl bg-amber-400/20 border border-amber-400/40 flex items-center justify-center">
                  <HandHeart size={28} className="text-amber-300" />
                </div>
                <h3 className="heading text-2xl font-black mt-5">Explore all programs</h3>
                <p className="mt-2 text-sm text-blue-100">See the full impact across all 5 focus areas and how you can contribute.</p>
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-black text-amber-300 group-hover:translate-x-1 transition">
                View all programs <ArrowRight size={14} />
              </div>
            </Link>
          </div>
        </div>
      </Reveal>

      {/* === Impact Stories === */}
      <Reveal as="section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.25em] text-[#F59E0B]">Voices of Impact</div>
            <h2 className="heading mt-3 text-3xl sm:text-4xl font-black">Stories from the ground.</h2>
          </div>
          <div className="flex items-center gap-1 text-sm text-slate-600">
            <Star size={15} className="fill-amber-400 text-amber-400" />
            <Star size={15} className="fill-amber-400 text-amber-400" />
            <Star size={15} className="fill-amber-400 text-amber-400" />
            <Star size={15} className="fill-amber-400 text-amber-400" />
            <Star size={15} className="fill-amber-400 text-amber-400" />
            <span className="ml-1 font-bold">4.9 / 5</span>
            <span className="text-slate-400">· 650+ donors</span>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          {STORIES.map((s, i) => (
            <div key={i} className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:-translate-y-1 hover:shadow-xl transition flex flex-col">
              <div className="relative h-48 overflow-hidden">
                <img src={s.img} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <Quote size={28} className="absolute top-4 right-4 text-amber-300 drop-shadow" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-slate-700 leading-relaxed italic">"{s.quote}"</p>
                <div className="mt-auto pt-5 flex items-center gap-3 border-t border-slate-100 mt-5">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1E3A8A] to-amber-400 flex items-center justify-center text-white font-black">
                    {s.name[0]}
                  </div>
                  <div>
                    <div className="heading font-black text-sm text-slate-900">{s.name}</div>
                    <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <MapPin size={11} /> {s.role}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      {/* === Gallery Marquee === */}
      <section className="bg-[#0b1637] text-white py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.25em] text-amber-300">Our Work</div>
            <h2 className="heading mt-3 text-3xl sm:text-4xl font-black">Moments from the Field</h2>
          </div>
          <Link to="/gallery" className="text-amber-300 font-black text-sm inline-flex items-center gap-1 hover:text-amber-200">
            Open full gallery <ArrowRight size={14} />
          </Link>
        </div>
        <div className="relative">
          <div className="flex gap-4 animate-marquee" style={{ width: "fit-content" }}>
            {[...PMR_PHOTOS, ...PMR_PHOTOS].map((p, i) => (
              <div key={i} className="w-72 h-56 rounded-2xl overflow-hidden shrink-0 border border-white/10">
                <img src={p} alt="PMR" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-20 bg-gradient-to-r from-[#0b1637] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-20 bg-gradient-to-l from-[#0b1637] to-transparent" />
        </div>
      </section>

      {/* === CTA Band === */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#F59E0B] via-[#F59E0B] to-[#D97706] text-white py-24">
        <div className="absolute -top-20 -right-20 w-96 h-96 bg-white/10 rounded-full animate-blob" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-white/10 rounded-full animate-blob" style={{ animationDelay: "4s" }} />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block px-4 py-1.5 rounded-full bg-white/15 backdrop-blur border border-white/30 text-white text-[11px] font-black uppercase tracking-[0.25em]">Get Involved</div>
          <h2 className="heading mt-5 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">Ready to make a difference?</h2>
          <p className="mt-5 text-lg text-orange-50 max-w-2xl mx-auto">
            Your donation provides education, healthcare, and food to those who need it most. Every rupee brings real change.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link to="/donate" className="shine-on-hover bg-white text-[#D97706] hover:bg-orange-50 font-black rounded-full px-9 py-4 inline-flex items-center gap-2 shadow-xl" data-testid="cta-donate-button">
              <Heart size={18} className="fill-[#D97706]" /> Donate Now
            </Link>
            <a href={`tel:${phone}`} className="bg-white/10 hover:bg-white/20 border-2 border-white/50 text-white font-black rounded-full px-9 py-4 inline-flex items-center gap-2">
              <Phone size={16} /> Call Us
            </a>
          </div>
        </div>
      </section>

      {/* === Newsletter === */}
      <section className="bg-[#0B1120] text-white py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-2xl shadow-amber-500/30">
            <Mail size={28} className="text-white" />
          </div>
          <h2 className="heading mt-6 text-4xl sm:text-5xl font-black">Stay Connected</h2>
          <p className="mt-4 text-slate-400">Subscribe for monthly impact reports, beneficiary stories, and upcoming events.</p>
          <form onSubmit={subscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 px-5 py-3.5 rounded-xl bg-slate-800 border border-slate-700 focus:border-amber-400 text-white placeholder:text-slate-500 outline-none transition"
              data-testid="newsletter-email-input"
            />
            <button type="submit" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white font-black rounded-xl px-6 py-3.5 inline-flex items-center justify-center gap-2 shadow-lg shadow-amber-500/30 transition" data-testid="newsletter-submit">
              <Send size={16} /> Subscribe
            </button>
          </form>
          <div className="mt-5 text-xs text-slate-500">No spam. Unsubscribe anytime.</div>
        </div>
      </section>

      <PublicFooter contact={hp} />
      <WhatsAppButton number={hp?.whatsapp_number || "919155561892"} />

      {videoOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setVideoOpen(false)}>
          <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setVideoOpen(false)} className="absolute -top-12 right-0 text-white hover:text-amber-400" data-testid="video-close-button">
              <X size={28} />
            </button>
            <iframe className="w-full h-full rounded-xl" src={videoUrl} title="PMR Welfare Foundation" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
          </div>
        </div>
      )}
    </div>
  );
}

function toEmbed(url) {
  if (!url) return "";
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1` : url;
}

function MiniStat({ k, v }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-slate-200 hover:border-brand transition">
      <div className="heading text-xl font-black text-brand">{k}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-1 font-bold">{v}</div>
    </div>
  );
}

function FounderCard({ name, designation, photo }) {
  const initials = (name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="rounded-3xl bg-gradient-to-b from-slate-50 to-white border border-slate-200 p-6 text-center hover:-translate-y-1 hover:shadow-xl transition" data-testid={`founder-card-${(name || "").replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-amber-200/50 bg-slate-100 flex items-center justify-center">
        {photo ? (
          <img src={photo} alt={name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-3xl font-black text-slate-400">{initials}</span>
        )}
      </div>
      <h3 className="heading mt-5 text-xl font-black">{name}</h3>
      <div className="mt-1 text-xs uppercase tracking-[0.2em] font-bold text-[#D97706]">{designation}</div>
      <div className="mt-4 flex items-center justify-center gap-1 text-xs text-slate-500">
        <span className="w-8 h-px bg-amber-300" />
        PMR Welfare Foundation
        <span className="w-8 h-px bg-amber-300" />
      </div>
    </div>
  );
}

function StatCard({ end, prefix = "", suffix = "", label, sub, accent, display, testid }) {
  const [ref, value] = useCountUp(end);
  return (
    <div ref={ref} className="relative rounded-2xl bg-white border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-xl transition overflow-hidden group" data-testid={testid}>
      <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${accent}`} />
      <div className={`heading text-4xl lg:text-5xl font-black bg-gradient-to-r ${accent} bg-clip-text text-transparent tabular-nums`}>
        {display || `${prefix}${value}${suffix}`}
      </div>
      <div className="heading font-black text-slate-900 mt-2">{label}</div>
      <div className="text-xs text-slate-500 mt-1">{sub}</div>
    </div>
  );
}

function Reveal({ as: Tag = "section", className = "", children }) {
  const [ref, inView] = useInView({ threshold: 0.1, once: true });
  return (
    <Tag ref={ref} className={`reveal ${inView ? "is-visible" : ""} ${className}`}>
      {children}
    </Tag>
  );
}
